import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert, Button, LinearProgress, Typography } from '@mui/material';
import { useDashboardData } from './hooks/useDashboardData';
import { SEARCH_DEBOUNCE_MS } from '../../config/constants';
import {
  OverviewMetrics,
  QuickInsights,
  CategoryAnalyticsAccordion,
  ModelAnalyticsAccordion,
  PerformanceSummaryAccordion,
  DashboardHeader,
  SkeletonLoader,
  AnalyticsChartsSection,
  TemplateSummaryTable,
} from './components';
import DateRangeDialog from './components/DateRangeDialog';

/**
 * PromptManagerDashboard Component
 *
 * A simplified dashboard for monitoring prompt management analytics including:
 * - Overview metrics (templates, executions, cost, success rate)
 * - Time period filtering (7d, 30d, 90d, MTD, custom)
 *
 * Features:
 * - Real-time data fetching with loading states
 * - Skeleton loading for better UX
 * - Error handling with retry mechanism
 * - Responsive design for mobile and desktop
 * - Quick insights with actionable recommendations
 */
const PromptManagerDashboard = () => {
  // State management for dashboard data and UI
  const [timePeriod, setTimePeriod] = useState('all-time');
  const [customRange, setCustomRange] = useState(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Pagination state for Template Summary table
  const [templatePerformancePage, setTemplatePerformancePage] = useState(1);
  const [templatePerformancePageSize, setTemplatePerformancePageSize] =
    useState(10);
  const [hideZeroActivity, setHideZeroActivity] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState('');
  const [templateSortBy, setTemplateSortBy] = useState('last_activity');
  const [templateSortOrder, setTemplateSortOrder] = useState('desc');

  // Debounce search input - only update debounced value after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplateSearch(templateSearch);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [templateSearch]);

  // Reset to page 1 when debounced search changes (after debounce completes)
  useEffect(() => {
    setTemplatePerformancePage(1);
  }, [debouncedTemplateSearch]);

  // Determine if using all-time (server-side pagination) or time-filtered (client-side)
  const isAllTime = timePeriod === 'all-time' && !customRange;

  // Tab state removed
  const {
    // Overall loading states
    // Core data (loaded first)
    coreLoading,
    coreError,
    overviewData,
    costBreakdown,
    // Secondary data (loaded in background)
    secondaryLoading,
    secondaryError,
    templatePerformance,
    topPerformers,
    categoryAnalytics,
    modelUsageDistribution,
    categoryAnalyticsData,
    adapters,
    // New chart data
    executionsPerWeek,
    providerDistribution,
    topModels,
    costComparison,
    // Refetch functions
    refetch,
    refetchSecondary,
  } = useDashboardData(
    timePeriod,
    customRange,
    isAllTime ? templatePerformancePage : 1, // Only use pagination for all-time
    isAllTime ? templatePerformancePageSize : 1000, // Large page size for time-filtered
    isAllTime && selectedCategory !== 'all' ? selectedCategory : null, // Only pass category for all-time
    isAllTime ? hideZeroActivity : false, // Only use server-side filter for all-time
    isAllTime ? debouncedTemplateSearch : '', // Only use server-side search for all-time (debounced)
    isAllTime ? templateSortBy : 'last_activity', // Only use server-side sorting for all-time
    isAllTime ? templateSortOrder : 'desc' // Only use server-side sorting for all-time
  );

  // Event handlers
  const handleTimePeriodChange = event => {
    const value = event.target.value;
    if (value === 'custom') {
      // First time selecting custom - open dialog immediately
      // (If clicking custom while already selected, onChange won't fire,
      // so this case is handled by onSelectClose in DashboardHeader)
      setIsDateRangeOpen(true);
    } else {
      // Close dialog if switching away from custom
      setIsDateRangeOpen(false);
    }
    setTimePeriod(value);
  };

  const handleApplyCustomRange = ({ startDate, endDate }) => {
    setCustomRange({ startDate, endDate });
    setIsDateRangeOpen(false);
  };

  const handleCancelCustomRange = () => {
    // If user cancels without selecting, revert to previous non-custom period if needed
    if (!customRange) {
      setTimePeriod('30d');
    }
    setIsDateRangeOpen(false);
  };

  const handleRefresh = () => {
    refetch();
  };

  // For all-time: use server-side pagination (templates already filtered/paginated by API)
  // For time-filtered: use client-side filtering/pagination
  const filteredTemplates = useMemo(() => {
    if (!templatePerformance?.templates) return [];

    if (isAllTime) {
      // Server-side pagination: templates are already filtered by category, hideZeroActivity, and paginated
      // No client-side filtering needed for all-time view
      return templatePerformance.templates;
    } else {
      // Client-side filtering for time-filtered view
      const byCategory =
        selectedCategory === 'all'
          ? templatePerformance.templates
          : templatePerformance.templates.filter(
              template => template.category === selectedCategory
            );
      return hideZeroActivity
        ? byCategory.filter(t => (t.execution_count || 0) > 0)
        : byCategory;
    }
  }, [
    templatePerformance?.templates,
    selectedCategory,
    hideZeroActivity,
    isAllTime,
  ]);

  // For all-time: templates are already paginated by server
  // For time-filtered: paginate client-side
  const paginatedTemplates = useMemo(() => {
    if (isAllTime) {
      // Server-side pagination: return templates as-is (already paginated)
      return filteredTemplates;
    } else {
      // Client-side pagination for time-filtered view
      const startIndex =
        (templatePerformancePage - 1) * templatePerformancePageSize;
      const endIndex = startIndex + templatePerformancePageSize;
      return filteredTemplates.slice(startIndex, endIndex);
    }
  }, [
    filteredTemplates,
    templatePerformancePage,
    templatePerformancePageSize,
    isAllTime,
  ]);

  // Total pages: use server-side pagination metadata for all-time, calculate for time-filtered
  const totalPages = useMemo(() => {
    if (isAllTime && templatePerformance?.pagination) {
      // Use server-side pagination metadata
      return templatePerformance.pagination.total_pages || 1;
    } else {
      // Client-side pagination calculation for time-filtered view
      return Math.ceil(filteredTemplates.length / templatePerformancePageSize);
    }
  }, [
    isAllTime,
    templatePerformance?.pagination,
    filteredTemplates.length,
    templatePerformancePageSize,
  ]);

  // Reset pagination when category or hideZeroActivity changes
  // Note: Search pagination reset is handled in the debounce effect above
  useEffect(() => {
    setTemplatePerformancePage(1);
  }, [selectedCategory, isAllTime, hideZeroActivity]);

  // Show core data immediately if available, even if secondary data is still loading
  const showCoreData = !coreLoading && (overviewData || costBreakdown);
  const showSecondaryData =
    !secondaryLoading &&
    (templatePerformance ||
      categoryAnalyticsData ||
      modelUsageDistribution ||
      topPerformers ||
      adapters ||
      executionsPerWeek ||
      providerDistribution ||
      topModels ||
      costComparison);

  return (
    <Box sx={{ mt: 4, mb: 4, px: 2, width: '100%' }}>
      {/* Dashboard Header */}
      <DashboardHeader
        timePeriod={timePeriod}
        customRange={customRange}
        selectedCategory={selectedCategory}
        categoryAnalytics={categoryAnalytics}
        loading={coreLoading}
        onTimePeriodChange={handleTimePeriodChange}
        onCategoryChange={e => setSelectedCategory(e.target.value)}
        onRefresh={handleRefresh}
        overviewData={overviewData}
        onSelectClose={() => {
          // Simply open the dialog when onSelectClose is called
          setIsDateRangeOpen(true);
        }}
      />

      {/* Custom Date Range Dialog */}
      <DateRangeDialog
        open={isDateRangeOpen}
        onApply={handleApplyCustomRange}
        onClose={handleCancelCustomRange}
        initialRange={customRange}
      />

      {/* Custom Date Range Dialog */}
      <DateRangeDialog
        open={isDateRangeOpen}
        onApply={handleApplyCustomRange}
        onClose={handleCancelCustomRange}
        initialRange={customRange}
      />

      {/* Core Error Display */}
      {coreError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {coreError}
        </Alert>
      )}

      {/* Secondary Error Display */}
      {secondaryError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={refetchSecondary}>
              Retry
            </Button>
          }
        >
          {secondaryError}
        </Alert>
      )}

      {/* Core Loading State - only show if no core data available */}
      {coreLoading && !showCoreData && (
        <Box sx={{ mb: 4 }}>
          <SkeletonLoader />
        </Box>
      )}

      {/* Core Dashboard Content - Show immediately when available */}
      {showCoreData && (
        <Box>
          {/* Overview Metrics */}
          <OverviewMetrics
            overviewData={overviewData}
            costBreakdown={costBreakdown}
          />

          {/* Quick Insights */}
          <QuickInsights
            overviewData={overviewData}
            templatePerformance={templatePerformance}
            costBreakdown={costBreakdown}
          />
        </Box>
      )}

      {/* Secondary Loading Indicator */}
      {secondaryLoading && !showSecondaryData && (
        <Box sx={{ mt: 4, textAlign: 'center', py: 4 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading detailed analytics...
          </Typography>
        </Box>
      )}

      {/* Analytics Charts Section */}
      <AnalyticsChartsSection
        executionsPerWeek={executionsPerWeek}
        providerDistribution={providerDistribution}
        topModels={topModels}
        costComparison={costComparison}
        costBreakdown={costBreakdown}
        loading={secondaryLoading}
      />

      {/* Category Analytics - Always render, show empty state if no data */}
      <CategoryAnalyticsAccordion
        categoryAnalyticsData={categoryAnalyticsData}
      />

      {/* Model Analytics - Always render, show empty state if no data */}
      <ModelAnalyticsAccordion
        modelUsageDistribution={modelUsageDistribution}
        adapters={adapters}
      />

      {/* Performance Summary - Always render, show empty state if no data */}
      <PerformanceSummaryAccordion
        topPerformers={topPerformers}
        adapters={adapters}
      />

      {/* Template Summary Table - Always render, show empty state if no data */}
      <TemplateSummaryTable
        templatePerformance={templatePerformance}
        isAllTime={isAllTime}
        templatePerformancePage={templatePerformancePage}
        templatePerformancePageSize={templatePerformancePageSize}
        totalPages={totalPages}
        onPageChange={setTemplatePerformancePage}
        onPageSizeChange={newSize => {
          setTemplatePerformancePageSize(newSize);
          setTemplatePerformancePage(1);
        }}
        hideZeroActivity={hideZeroActivity}
        onHideZeroActivityChange={value => {
          setHideZeroActivity(value);
          setTemplatePerformancePage(1);
        }}
        selectedCategory={selectedCategory}
        templateSearch={templateSearch}
        onTemplateSearchChange={setTemplateSearch}
        templateSortBy={templateSortBy}
        templateSortOrder={templateSortOrder}
        onSortByChange={value => {
          setTemplateSortBy(value);
          setTemplatePerformancePage(1);
        }}
        onSortOrderToggle={() => {
          setTemplateSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
          setTemplatePerformancePage(1);
        }}
        paginatedTemplates={paginatedTemplates}
      />
    </Box>
  );
};

export default PromptManagerDashboard;
